import { SkeletonLoader } from '@toggle/design-system';
import { PriceStatus } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import { addDays, format } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceDisplay } from '~/react-query-hooks/prices/usePriceDisplay';

import * as S from './InsightsLivePriceChange.styles';

export interface InsightsLivePriceChangeProps {
  entity: Entity;
  proportionChange?: number;
  prevCloseDate?: string;
}

export const InsightsLivePriceChange = ({
  entity,
  proportionChange,
  prevCloseDate,
}: InsightsLivePriceChangeProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  if (isLoading) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData) {
    return null;
  }

  const { price } = priceData;

  const getSinglePriceDisplay = () => {
    const marketHours = price.primary.marketHours;
    const priceChange = price.primary.priceChange;

    if (proportionChange !== undefined) {
      const date = prevCloseDate && format(prevCloseDate, 'MMM d');
      const sign = Math.sign(proportionChange) >= 0 ? '+' : '-';
      return (
        <>
          <S.PriceChangeContainer
            $status={
              proportionChange >= 0
                ? PriceStatus.Positive
                : PriceStatus.Negative
            }
            data-testid="proportion-change"
          >
            <S.PercentageChangeLabel>{`${sign}${Math.abs(
              proportionChange
            ).toFixed(2)}%`}</S.PercentageChangeLabel>
          </S.PriceChangeContainer>
          {date && (
            <S.DateSpan>
              {t('analyze:analysis.sinceDateClose', {
                date,
              })}
            </S.DateSpan>
          )}
        </>
      );
    }

    if (
      marketHours.isPreMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive &&
      price.secondary.priceChange
    ) {
      const priceChange = price.secondary.priceChange;
      return (
        <>
          <S.PriceChangeContainer
            $status={priceChange.status}
            data-testid="price-change"
          >
            <S.PercentageChangeLabel>
              {priceChange?.proportionChange}
            </S.PercentageChangeLabel>
          </S.PriceChangeContainer>
          <S.LiveLabel>{t('analyze:analysis.preMarket')}</S.LiveLabel>
        </>
      );
    }

    if (
      marketHours.isAfterMarket &&
      price.secondary?.price !== undefined &&
      price.secondary?.isLive &&
      price.secondary.priceChange
    ) {
      const priceChange = price.secondary.priceChange;
      return (
        <>
          <S.PriceChangeContainer
            $status={priceChange.status}
            data-testid="price-change"
          >
            <S.PercentageChangeLabel>
              {price.secondary.priceChange?.proportionChange}
            </S.PercentageChangeLabel>
          </S.PriceChangeContainer>
          <S.LiveLabel>{t('analyze:analysis.after-hours')}</S.LiveLabel>
        </>
      );
    }

    if (
      marketHours.isMarketHours &&
      price.primary?.isLive &&
      price.primary.priceChange
    ) {
      const priceChange = price.primary.priceChange;
      return (
        <>
          <S.PriceChangeContainer
            $status={priceChange.status}
            data-testid="price-change"
          >
            <S.PercentageChangeLabel>
              {price.primary.priceChange?.proportionChange}
            </S.PercentageChangeLabel>
          </S.PriceChangeContainer>
          <S.LiveLabel>{t('analyze:analysis.marketOpen')}</S.LiveLabel>
        </>
      );
    }

    const date = format(addDays(new Date(), -1), 'MMM d');
    return priceChange ? (
      <>
        <S.PriceChangeContainer
          $status={priceChange.status}
          data-testid="price-change"
        >
          <S.PercentageChangeLabel>
            {price.primary.priceChange?.proportionChange}
          </S.PercentageChangeLabel>
        </S.PriceChangeContainer>
        <S.DateSpan>
          {t('analyze:analysis.sinceDateClose', {
            date,
          })}
        </S.DateSpan>
      </>
    ) : null;
  };

  return (
    <div data-testid="insight-live-price-change">
      <S.FlexContainer>{getSinglePriceDisplay()}</S.FlexContainer>
    </div>
  );
};
